import { CommonModule } from '@angular/common';
import { Component, ElementRef, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Observable, concatMap, forkJoin, from, of } from 'rxjs';
import { catchError, map, switchMap, toArray } from 'rxjs/operators';
import * as XLSX from 'xlsx';
import { DesignPartnerDomainsService } from '../../../services/design-partner-domains.service';
import { ToastService } from '../../../services/toast.service';

interface DomainProcessResult {
  domain: string;
  success: boolean;
  message?: string;
}

@Component({
  selector: 'app-add-domain',
  standalone: true,
  imports: [FormsModule, CommonModule],
  templateUrl: './add-domain.component.html',
  styleUrl: './add-domain.component.less',
})
export class AddDomainComponent {
  domain: string = '';
  isSubmitting: boolean = false;

  showFormatModal: boolean = false;

  isUploading: boolean = false;
  uploadTotal: number = 0;
  uploadProcessed: number = 0;

  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

  constructor(
    private domainsService: DesignPartnerDomainsService,
    private toaster: ToastService,
    private router: Router
  ) {}

  goBack(): void {
    this.router.navigate(['/domains']);
  }

  getDaysRemaining(notAfter: string): number {
    return Math.ceil((new Date(notAfter).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  }

  calculateRisk(cert: any, tls: any[]): string {
    const days = this.getDaysRemaining(cert.notAfter);
    const insecureTls = tls.some(
      (t: any) => (t.version === 'TLSv1' || t.version === 'TLSv1.1') && t.supported
    );
    if (days < 0 || days <= 14) return 'CRITICAL';
    if (days <= 30 || insecureTls) return 'HIGH';
    if (days <= 90) return 'MEDIUM';
    return 'LOW';
  }

  submitDomain(): void {
    const trimmed = this.domain.trim();
    if (!trimmed) {
      this.toaster.showToast('Please enter a domain name', 'error');
      return;
    }

    this.isSubmitting = true;
    this.processDomain(trimmed).subscribe((result) => {
      this.isSubmitting = false;
      if (result.success) {
        this.toaster.showToast('Domain added successfully', 'success');
        this.router.navigate(['/domains', trimmed]);
      } else {
        this.toaster.showToast(result.message || 'Failed to add domain', 'error');
      }
    });
  }

  /**
   * Runs the full "Search Domain" pipeline for a single domain (register, scan CT logs,
   * fetch TLS per record, save). Always resolves rather than erroring so batch callers
   * (bulk upload) can process every domain even if some fail.
   */
  private processDomain(trimmed: string): Observable<DomainProcessResult> {
    return this.domainsService.addDomain(trimmed).pipe(
      switchMap(() =>
        this.domainsService.searchCertificates(trimmed).pipe(
          switchMap((response: any) => {
            const results: any[] = response?.results ?? [];

            const latestByDomain = new Map<string, any>();
            for (const cert of results) {
              const key = cert?.commonName;
              if (!key) continue;
              const existing = latestByDomain.get(key);
              if (!existing || new Date(cert.insertedTime) > new Date(existing.insertedTime)) {
                latestByDomain.set(key, cert);
              }
            }
            const uniqueRecords = Array.from(latestByDomain.values());

            const tlsCalls = uniqueRecords.map((cert) =>
              this.domainsService.getTlsInfo(cert.commonName).pipe(
                catchError(() => of({ domain: cert.commonName, tls: [] }))
              )
            );
            const tls$ = tlsCalls.length > 0 ? forkJoin(tlsCalls) : of([]);

            return tls$.pipe(
              switchMap((tlsResponses: any[]) => {
                const tlsMap: { [key: string]: any[] } = {};
                tlsResponses.forEach((res: any, idx: number) => {
                  const key = uniqueRecords[idx]?.commonName;
                  if (key) tlsMap[key] = res?.tls ?? [];
                });

                const toSave = uniqueRecords.map((cert: any) => {
                  const tls = tlsMap[cert.commonName] ?? [];
                  return { ...cert, tls, riskLevel: this.calculateRisk(cert, tls) };
                });

                return this.domainsService.saveSubDomains(trimmed, toSave).pipe(
                  map(() => ({ domain: trimmed, success: true }) as DomainProcessResult),
                  catchError((err) => {
                    const message = err?.error?.message || 'Domain added but failed to save records';
                    return of({ domain: trimmed, success: false, message } as DomainProcessResult);
                  })
                );
              }),
              catchError(() =>
                of({ domain: trimmed, success: false, message: 'Failed to fetch TLS information' } as DomainProcessResult)
              )
            );
          }),
          catchError((err) => {
            const message = err?.error?.message || 'Domain saved but failed to fetch certificates';
            return of({ domain: trimmed, success: false, message } as DomainProcessResult);
          })
        )
      ),
      catchError((err) => {
        const message = err?.error?.message || err?.error?.error || 'Failed to add domain';
        return of({ domain: trimmed, success: false, message } as DomainProcessResult);
      })
    );
  }

  // ---------- Download sample ----------

  openDownloadSampleModal(): void {
    this.showFormatModal = true;
  }

  closeFormatModal(): void {
    this.showFormatModal = false;
  }

  downloadSample(format: 'csv' | 'excel'): void {
    const rows = [['Domains'], ['example.com']];

    if (format === 'csv') {
      const csvContent = rows.map((r) => r.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      this.triggerDownload(blob, 'domains_sample.csv');
    } else {
      const worksheet = XLSX.utils.aoa_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Domains');
      XLSX.writeFile(workbook, 'domains_sample.xlsx');
    }

    this.closeFormatModal();
  }

  private triggerDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }

  // ---------- Upload ----------

  triggerUpload(): void {
    if (this.isUploading) return;
    this.fileInput?.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      let domains: string[] = [];
      try {
        domains = this.extractDomainsFromFile(reader.result as ArrayBuffer);
      } catch {
        this.toaster.showToast('Failed to read the uploaded file. Please check the format.', 'error');
        return;
      }
      this.handleUploadedDomains(domains);
    };
    reader.onerror = () => {
      this.toaster.showToast('Failed to read the uploaded file.', 'error');
    };
    reader.readAsArrayBuffer(file);
  }

  private extractDomainsFromFile(data: ArrayBuffer): string[] {
    const workbook = XLSX.read(data, { type: 'array' });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const rows: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });

    if (!rows.length) return [];

    let startRow = 0;
    let columnIndex = 0;

    const headerRow = (rows[0] || []).map((cell) => (cell ?? '').toString().trim().toLowerCase());
    const headerIndex = headerRow.findIndex((cell) => cell === 'domains' || cell === 'domain');
    if (headerIndex !== -1) {
      columnIndex = headerIndex;
      startRow = 1;
    }

    const domainPattern = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i;
    const seen = new Set<string>();
    const domains: string[] = [];

    for (let i = startRow; i < rows.length; i++) {
      const raw = (rows[i]?.[columnIndex] ?? '').toString().trim();
      if (!raw || !domainPattern.test(raw)) continue;
      const key = raw.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      domains.push(raw);
    }

    return domains;
  }

  private handleUploadedDomains(domains: string[]): void {
    if (domains.length === 0) {
      this.toaster.showToast('No valid domains found in the uploaded file', 'error');
      return;
    }

    this.isUploading = true;
    this.domainsService.getDomains().subscribe({
      next: (response: any) => {
        const existing: any[] = response?.data ?? response ?? [];
        const existingSet = new Set(
          (Array.isArray(existing) ? existing : []).map((item) =>
            (item.domain || item.name || '').toString().trim().toLowerCase()
          )
        );

        const newDomains = domains.filter((d) => !existingSet.has(d.toLowerCase()));

        if (newDomains.length === 0) {
          this.isUploading = false;
          this.toaster.showToast('All domains in the file are already registered', 'info');
          return;
        }

        this.processUploadedDomains(newDomains);
      },
      error: () => {
        this.isUploading = false;
        this.toaster.showToast('Failed to check existing domains', 'error');
      },
    });
  }

  private processUploadedDomains(domains: string[]): void {
    this.uploadTotal = domains.length;
    this.uploadProcessed = 0;

    from(domains)
      .pipe(
        concatMap((d) =>
          this.processDomain(d).pipe(
            map((result) => {
              this.uploadProcessed++;
              return result;
            })
          )
        ),
        toArray()
      )
      .subscribe((results) => {
        this.isUploading = false;
        const succeeded = results.filter((r) => r.success).length;
        const failed = results.length - succeeded;

        if (failed === 0) {
          this.toaster.showToast(`${succeeded} domain(s) added successfully`, 'success');
        } else if (succeeded === 0) {
          this.toaster.showToast(`Failed to add ${failed} domain(s)`, 'error');
        } else {
          this.toaster.showToast(`${succeeded} domain(s) added, ${failed} failed`, 'info');
        }

        this.router.navigate(['/domains']);
      });
  }
}
